import { MISSION_STATUS_LABELS } from "../api";
import type { MissionProgress } from "../api";
import { Badge, Card, EmptyState, Table } from "../components";
import type { TableColumn } from "../components";
import { formatDate, formatTokens, isoDateTime } from "../format";
import styles from "./MissionsPage.module.css";

const TITRE = "Projets clos et annulés";
const SOUS_TITRE =
  "Ce qui a été consommé, et ce qui est revenu dans votre solde à la clôture.";
// La carte porte déjà un titre et un sous-titre qui décrivent ce tableau :
// afficher la légende en plus empilerait deux descriptions de la même chose.
// Elle reste dans le DOM, seulement masquée à l'œil — un lecteur d'écran qui
// tombe sur un <table> sans <caption> ne sait pas ce qu'il lit.
const LEGENDE =
  "Missions terminées du portefeuille : statut, enveloppe commandée, tokens imputés, reliquat relâché et date de clôture.";

/*
 * Colonnes définies une fois, hors du composant : les recréer à chaque rendu
 * ferait de `columns` une référence neuve à chaque fois, pour rien.
 *
 * Aucune colonne d'action ici, et c'est le fond du sujet : une mission close ou
 * annulée refuse les trois gestes (`409 invalid_transition`). Un bouton qui ne
 * produirait qu'un refus n'a pas à être offert.
 */
const COLONNES: TableColumn<MissionProgress>[] = [
  {
    key: "mission",
    header: "Projet",
    render: (m) => (
      <div className={styles.missionTexte}>
        <span className={styles.missionLibelle}>{m.label}</span>
        <time className={styles.missionDate} dateTime={isoDateTime(m.created_at)}>
          Ouvert le {formatDate(m.created_at)}
        </time>
      </div>
    ),
  },
  {
    key: "statut",
    header: "Statut",
    width: "8rem",
    render: (m) => (
      // Le texte porte l'information, la couleur ne fait que la rappeler : un
      // badge ne se lit pas en noir et blanc.
      <Badge tone={m.status === "completed" ? "success" : "neutral"} dot>
        {MISSION_STATUS_LABELS[m.status]}
      </Badge>
    ),
  },
  {
    key: "envelope",
    header: "Budget",
    numeric: true,
    width: "8rem",
    render: (m) => formatTokens(m.envelope_tokens),
  },
  {
    key: "consumed",
    header: "Consommé",
    numeric: true,
    width: "8rem",
    render: (m) => formatTokens(m.consumed_tokens),
  },
  {
    // Sur une mission close, le reliquat n'est plus immobilisé : il a été
    // relâché vers le disponible dans la transaction de clôture. La colonne le
    // dit avec le mot juste, sinon on lit « reliquat » et on croit que des
    // tokens dorment encore quelque part.
    key: "released",
    header: "Relâché",
    numeric: true,
    width: "8rem",
    render: (m) => formatTokens(m.remaining_tokens),
  },
  {
    key: "closed",
    header: "Close le",
    width: "10rem",
    render: (m) =>
      m.closed_at === null ? (
        "—"
      ) : (
        <time dateTime={isoDateTime(m.closed_at)}>{formatDate(m.closed_at)}</time>
      ),
  },
];

export type MissionsClosesProps = {
  /** Missions `completed` et `cancelled`, dans l'ordre rendu par l'API. */
  missions: MissionProgress[];
};

/**
 * L'histoire terminée du portefeuille.
 *
 * Un tableau et non des cartes : sans action à porter, ces lignes se comparent
 * mieux alignées qu'empilées.
 */
export function MissionsCloses({ missions }: MissionsClosesProps) {
  return (
    <Card title={TITRE} subtitle={SOUS_TITRE} padding="none">
      <Table
        caption={<span className="lde-visually-hidden">{LEGENDE}</span>}
        columns={COLONNES}
        rows={missions}
        rowKey={(m) => m.id}
        empty={
          <EmptyState
            compact
            title="Aucune mission close."
            description="Rien n'a encore été clôturé ni annulé sur ce portefeuille."
          />
        }
      />
    </Card>
  );
}
